import axios from "axios";
import { APP_PATH } from "../api/backendPath";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000, // 30 seconds timeout
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("usr_1a2b3c");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error.response?.data || error.message);
    return Promise.reject(error);
  },
);

/**
 * Get lab reports with comprehensive details
 * @param {Object} params - Query parameters for filtering and pagination
 * @param {string} [params.reportStatus] - Filter by report status (NOT_SENT, SENT)
 * @param {string} [params.date] - Filter by service date (YYYY-MM-DD)
 * @param {boolean} [params.urgency] - Filter by urgency
 * @param {string} [params.location] - Filter by service location (HOME_VISIT, ON_SITE)
 * @param {string} [params.search] - Search by booking number or patient name
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=10] - Items per page
 * @returns {Promise<Object>} Response with reports list and pagination
 */
export const getLabReports = async (params = {}) => {
  try {
    console.log("Fetching lab reports with params:", params);

    const response = await api.get(APP_PATH.getLabReports, {
      params: {
        page: 1,
        limit: 10,
        ...params,
      },
    });

    console.log("Lab reports response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching lab reports:", error);
    throw error;
  }
};

/**
 * Get detailed information about a specific lab report
 * @param {string} reportId - Report/Order ID
 * @returns {Promise<Object>} Response with comprehensive report details
 */
export const getReportDetails = async (reportId) => {
  try {
    console.log("Fetching report details for ID:", reportId);

    const response = await api.get(`${APP_PATH.getReportDetails}/${reportId}`);

    console.log("Report details response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching report details:", error);
    throw error;
  }
};

/**
 * Resend lab report to patient
 * @param {string} reportId - Report/Order ID
 * @returns {Promise<Object>} Response with resend confirmation
 */
export const resendLabReport = async (reportId) => {
  try {
    console.log("Resending lab report for ID:", reportId);

    const response = await api.post(
      `${APP_PATH.resendReport}/${reportId}/resend`,
    );

    console.log("Resend report response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error resending lab report:", error);
    throw error;
  }
};

/**
 * Export lab reports data (if needed for future implementation)
 * @param {Object} params - Export parameters
 * @returns {Promise<Blob>} Response with file data
 */
export const exportLabReports = async (params = {}) => {
  try {
    console.log("Exporting lab reports with params:", params);

    const response = await api.get(`${APP_PATH.getLabReports}/export`, {
      params,
      responseType: "blob", // For file download
    });

    console.log("Export reports response received");
    return response.data;
  } catch (error) {
    console.error("Error exporting lab reports:", error);
    throw error;
  }
};

// Default export
export default {
  getLabReports,
  getReportDetails,
  resendLabReport,
  exportLabReports,
};
